import { AbilityType, type AbilityDef } from "./classes";

export type AbilityTarget = {
  id: number;
  x: number;
  y: number;
  z: number;
  /** Horizontal hit radius of the target's body. */
  radius: number;
  height: number;
};

export type AbilityHit = {
  targetId: number;
  damage: number;
};

const PROJECTILE_HIT_RADIUS = 0.6;

/**
 * Pure hit resolution for an ability cast against a set of targets.
 * PROJECTILE/DASH hit the first target along the aim line;
 * AREA hits everything within range around the caster; BUFF hits nothing.
 */
export function computeAbilityHits(
  ability: AbilityDef,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  targets: AbilityTarget[]
): AbilityHit[] {
  if (ability.damage <= 0) return [];

  switch (ability.type) {
    case AbilityType.PROJECTILE:
      return firstAlongRay(ability, origin, dir, targets, ability.range);
    case AbilityType.DASH:
      // A dash damages whatever the caster passes through.
      return firstAlongRay(ability, origin, dir, targets, Math.max(ability.range, 4));
    case AbilityType.AREA:
      return withinArea(ability, origin, targets);
    default:
      return [];
  }
}

function firstAlongRay(
  ability: AbilityDef,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  targets: AbilityTarget[],
  range: number
): AbilityHit[] {
  const len = Math.sqrt(dir.x * dir.x + dir.y * dir.y + dir.z * dir.z);
  if (len === 0) return [];
  const dx = dir.x / len;
  const dy = dir.y / len;
  const dz = dir.z / len;

  let best: { targetId: number; t: number } | null = null;

  for (const target of targets) {
    // Aim at the target's body center
    const cx = target.x - origin.x;
    const cy = target.y + target.height / 2 - origin.y;
    const cz = target.z - origin.z;

    // Projection of target center onto the ray
    const t = cx * dx + cy * dy + cz * dz;
    if (t < 0 || t > range) continue;

    // Perpendicular distance from target center to the ray
    const px = cx - dx * t;
    const py = cy - dy * t;
    const pz = cz - dz * t;
    const dist = Math.sqrt(px * px + py * py + pz * pz);

    if (dist <= target.radius + PROJECTILE_HIT_RADIUS) {
      if (!best || t < best.t) {
        best = { targetId: target.id, t };
      }
    }
  }

  return best ? [{ targetId: best.targetId, damage: ability.damage }] : [];
}

function withinArea(
  ability: AbilityDef,
  origin: { x: number; y: number; z: number },
  targets: AbilityTarget[]
): AbilityHit[] {
  const hits: AbilityHit[] = [];
  for (const target of targets) {
    const dx = target.x - origin.x;
    const dz = target.z - origin.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    // Vertical bound keeps ground AoE from hitting mobs in caves below
    const dy = target.y + target.height / 2 - origin.y;
    if (dist <= ability.range + target.radius && Math.abs(dy) <= ability.range + target.height) {
      hits.push({ targetId: target.id, damage: ability.damage });
    }
  }
  return hits;
}
